import React, { useEffect, useMemo, useState } from 'react'
import { Form, Modal } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { FaExchangeAlt, FaShieldAlt, FaTimesCircle } from 'react-icons/fa'
import { useThemeMode } from '../../../_metronic/partials/layout/theme-mode/ThemeModeProvider'
import {
  useUserMergeLineItemsV2Mutation,
  useUserMergeSSLineItemsV2Mutation,
} from '../../../services/ExpenseApi'
import MergeSelectedLineItemHeader, {
  formatReceiptNumberDisplay,
  getReceiptNumber,
} from './MergeSelectedLineItemHeader'
import { buildMergedLineItemForPreview, buildFinalItemDescription, getExistingItemDescription } from './mergeFinalPreview'

export type MergeV2Mode = 'bank' | 'ss'

type Props = {
  show: boolean
  handleClose: () => void
  onConfirm: () => void
  mergeMode: MergeV2Mode
  /** Resolved From (non-Bank when Bank is involved) */
  selectedDraftData?: any
  /** Resolved Into (kept) */
  selectedTargetData?: any
  canSwap?: boolean
  onSwap?: () => void
  refetch?: () => void
  onSuccess?: () => void
}

const MergeLineItemConfirmationModalV2 = ({
  show,
  handleClose,
  onConfirm,
  mergeMode,
  selectedDraftData,
  selectedTargetData,
  canSwap = false,
  onSwap,
  refetch,
  onSuccess,
}: Props) => {
  const { mode: themeMode } = useThemeMode()
  const isMergeDark = themeMode === 'dark'
  const [mergeBank, { data: bankData, isSuccess: bankOk, isError: bankErr, error: bankError, isLoading: bankLoading, reset: resetBank }] =
    useUserMergeLineItemsV2Mutation()
  const [mergeSS, { data: ssData, isSuccess: ssOk, isError: ssErr, error: ssError, isLoading: ssLoading, reset: resetSS }] =
    useUserMergeSSLineItemsV2Mutation()

  const [hasProcessedSuccess, setHasProcessedSuccess] = useState(false)
  const [isConfirmed, setIsConfirmed] = useState(false)
  const [itemDescription, setItemDescription] = useState('')

  const isLoading = bankLoading || ssLoading
  const isSuccess = mergeMode === 'bank' ? bankOk : ssOk
  const isError = mergeMode === 'bank' ? bankErr : ssErr
  const error = mergeMode === 'bank' ? bankError : ssError
  const data = mergeMode === 'bank' ? bankData : ssData

  const fromItem = selectedDraftData
  const intoItem = selectedTargetData

  const handleMergeLineItem = async () => {
    try {
      const description = buildFinalItemDescription(itemDescription, fromItem)
      if (mergeMode === 'bank') {
        await mergeBank({
          CCExpenseItemID: intoItem?.expenseItemId,
          SSExpenseItemID: fromItem?.expenseItemId,
          UserComments: description || undefined,
        })
      } else {
        await mergeSS({
          FromExpenseItemID: fromItem?.expenseItemId,
          IntoExpenseItemID: intoItem?.expenseItemId,
          UserComments: description || undefined,
        })
      }
    } catch (e) {
      console.error(e)
    }
  }

  useEffect(() => {
    if (isSuccess && !hasProcessedSuccess) {
      setHasProcessedSuccess(true)
      toast.success((data as any)?.message || 'Merge completed')
      if (refetch) refetch()
      if (onSuccess) onSuccess()
      handleClose()
      onConfirm()
    }
  }, [isSuccess, hasProcessedSuccess])

  useEffect(() => {
    if (isError && error) {
      let errorMessage = 'Something went wrong'
      if ('data' in error && error.data && typeof error.data === 'object' && 'message' in error.data) {
        errorMessage = (error.data as { message: string }).message
      }
      toast.error(errorMessage)
    }
  }, [isError, error])

  useEffect(() => {
    if (show) {
      setHasProcessedSuccess(false)
      setIsConfirmed(false)
      setItemDescription(
        fromItem && intoItem ? getExistingItemDescription(fromItem, intoItem) : ''
      )
    }
  }, [show, fromItem?.expenseItemId, intoItem?.expenseItemId, mergeMode])

  useEffect(() => {
    if (!show) {
      resetBank()
      resetSS()
    }
  }, [show, resetBank, resetSS])

  const fromReceipt = getReceiptNumber(fromItem)
  const intoReceipt = getReceiptNumber(intoItem)
  const fromReceiptDisplay = formatReceiptNumberDisplay(fromReceipt)
  const intoReceiptDisplay = formatReceiptNumberDisplay(intoReceipt)

  const confirmMatchHints = useMemo(() => {
    if (!fromItem || !intoItem) return undefined
    const preferAmount = Number(String(fromItem?.amount ?? '').replace(/[^0-9.-]/g, ''))
    const amountMatch =
      Number.isFinite(preferAmount) &&
      preferAmount > 0 &&
      Math.abs(Number(String(intoItem?.amount ?? '').replace(/[^0-9.-]/g, '')) - preferAmount) < 0.001
    const dateMatch =
      !!fromItem?.itemDate &&
      !!intoItem?.itemDate &&
      String(fromItem.itemDate).split('T')[0] === String(intoItem.itemDate).split('T')[0]
    return {
      dateMatch,
      amountMatch,
      isBestMatch: dateMatch && amountMatch,
    }
  }, [fromItem, intoItem])

  const mergedPreviewItem = useMemo(
    () =>
      fromItem && intoItem
        ? buildMergedLineItemForPreview(fromItem, intoItem, mergeMode, itemDescription)
        : null,
    [fromItem, intoItem, mergeMode, itemDescription]
  )

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      fullscreen
      dialogClassName="merge-confirm-v2-dialog ss-modal-100"
      contentClassName={`merge-confirm-v2-content${isMergeDark ? ' merge-theme-dark' : ''}`}
    >
      <style>{`
        .modal:has(> .merge-confirm-v2-dialog) {
          padding: 0 !important;
        }
        .merge-confirm-v2-dialog {
          width: 100% !important;
          max-width: 100% !important;
          height: 100% !important;
          margin: 0 !important;
        }
        .merge-confirm-v2-content {
          height: 100%;
          min-height: 100%;
          display: flex;
          flex-direction: column;
          border-radius: 0;
          border: 0;
        }
        .merge-confirm-v2-content .modal-body {
          overflow-y: auto;
          flex: 1 1 auto;
          position: relative;
          scrollbar-width: thin;
          scrollbar-color: #009ef7 #d8d8e1;
        }
        .merge-confirm-v2-content .modal-body::-webkit-scrollbar {
          width: 10px;
        }
        .merge-confirm-v2-content .modal-body::-webkit-scrollbar-track {
          background: #d8d8e1;
          border-radius: 8px;
        }
        .merge-confirm-v2-content .modal-body::-webkit-scrollbar-thumb {
          background: #009ef7;
          border-radius: 8px;
          border: 2px solid #d8d8e1;
        }
        .merge-confirm-v2-content .modal-body::-webkit-scrollbar-thumb:hover {
          background: #0077c5;
        }
      `}</style>
      <div className="modal-header">
        <h5 className="modal-title">Confirm Merge (V2)</h5>
        <button type="button" className="btn-close" onClick={handleClose}></button>
      </div>
      <div className="modal-body px-1">
        <p className="mb-3" style={{ fontSize: 13 }}>
          {mergeMode === 'bank'
            ? 'Merge selected SpendSail line item into the Bank line item (Bank is kept; SS is soft-deleted).'
            : 'Merge selected SpendSail line item into another SpendSail line item (Into is kept; From will be soft-deleted after confirm merge).'}
        </p>

        {fromItem && intoItem ? (
          <>
            <div className="d-flex flex-nowrap align-items-stretch" style={{ gap: 1 }}>
              <div style={{ flex: '1 1 0', minWidth: 0 }}>
                <MergeSelectedLineItemHeader
                  selectedDraftData={fromItem}
                  title={`Merge From — Receipt # ${fromReceiptDisplay}`}
                  variant="from"
                  matchHints={confirmMatchHints}
                />
              </div>
              <div
                className="d-flex flex-column align-items-center justify-content-center"
                style={{ flex: '0 0 auto', width: 40, padding: '0 1px' }}
              >
                <button
                  type="button"
                  className={`btn btn-sm btn-icon ${canSwap ? 'btn-primary' : 'btn-light'}`}
                  disabled={!canSwap || !onSwap}
                  title={
                    canSwap
                      ? 'Swap Merge From and Into (only when neither is Bank)'
                      : 'Bank is always kept (Into) — swap is only available when neither line item is Bank'
                  }
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    onSwap?.()
                  }}
                >
                  <FaExchangeAlt />
                </button>
                <span className="text-muted text-center" style={{ fontSize: 10 }}>
                  Swap
                </span>
                {!canSwap ? (
                  <span className="text-muted text-center" style={{ fontSize: 9 }}>
                    Bank always Into
                  </span>
                ) : null}
              </div>
              <div style={{ flex: '1 1 0', minWidth: 0 }}>
                <MergeSelectedLineItemHeader
                  selectedDraftData={intoItem}
                  title={`Merge To — Receipt # ${intoReceiptDisplay}${
                    mergeMode === 'bank' ? ' (Bank)' : ''
                  }`}
                  variant="into"
                  matchHints={confirmMatchHints}
                />
              </div>
            </div>

            {mergedPreviewItem ? (
              <MergeSelectedLineItemHeader
                selectedDraftData={mergedPreviewItem}
                title="Merged Result (Preview)"
                variant="preview"
                matchHints={confirmMatchHints}
              />
            ) : null}

            <Form.Group className="mt-3 mb-2">
              <Form.Label className="fw-semibold" style={{ fontSize: 12 }}>
                Item Description
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                maxLength={2000}
                placeholder="Existing item description — add any details; merge note is appended in the preview and on save."
                value={itemDescription}
                onChange={(e) => setItemDescription(e.target.value)}
                style={{ fontSize: 12 }}
              />
              <Form.Text className="text-muted">
                Preview appends merge details after your text. {itemDescription.length}/2000
              </Form.Text>
            </Form.Group>
            <div className="form-check mb-1">
              <input
                type="checkbox"
                className="form-check-input"
                id="confirmCheckboxV2"
                checked={isConfirmed}
                onChange={(e) => setIsConfirmed(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="confirmCheckboxV2" style={{ fontSize: 12 }}>
                I confirm that I want to proceed with this merge.
              </label>
            </div>
          </>
        ) : null}
      </div>
      <div
        className="modal-footer merge-modal-footer-bar d-flex flex-wrap align-items-center justify-content-between gap-3"
      >
        <div className="d-flex flex-wrap align-items-center gap-3 gap-lg-4" style={{ fontSize: 12 }}>
          <span className="d-inline-flex align-items-center gap-2 text-gray-700">
            <FaTimesCircle className="text-danger" size={14} />
            Merge From (Soft Delete)
          </span>
          <span className="d-inline-flex align-items-center gap-2 text-gray-700">
            <FaShieldAlt className="text-success" size={14} />
            Bank is always kept (into)
          </span>
          <span className="d-inline-flex align-items-center gap-2 text-gray-700">
            <FaExchangeAlt className="text-muted" size={14} />
            Swap Merge From and Into (only when neither is Bank)
          </span>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button type="button" className="btn btn-light" onClick={handleClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleMergeLineItem}
            disabled={isLoading || !isConfirmed}
          >
            {!isLoading && 'Confirm Merge'}
            {isLoading && (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Merging...
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default MergeLineItemConfirmationModalV2
